import { Button, Grid, Typography } from '@mui/material';
import { PaymentMethodsService } from 'api/paymentMethods';
import AttachFile from 'components/common/AttachFile';
import FormInput from 'components/common/Form/Input';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import useTranslates from 'utils/translate';
import useDebounce from 'hooks/useDebounce';
import useMount from 'hooks/useMount';
import { PAYMENT_INFO_DELAY } from './constants';
import { LoadingButton } from '@mui/lab';
import useToaster from 'hooks/useToaster';
import useAnalytics from 'hooks/useAnalytics';
import { AnalyticsKey, AnalyticsPageViewEvents } from 'hooks/useAnalytics/enum';
import useCurrentUser from 'store/currentUser';
import useSettings, { IWebsiteConfigs } from 'store/settings';
import { useSearchParams } from 'react-router-dom';
import FileCopyIcon from '@mui/icons-material/FileCopy';

const AutomaticDeposit = ({
  watch,
  control,
  onClose,
  setValue,
  getValues,
  getInfoProperties,
  paymentMethod,
  changeAmountFieldVisibility,
  changeActionButtonVisibility,
}) => {
  const amount = watch('amount');
  const confirmationCode = watch('confirmationCode');
  const ticketId = watch('ticketId');

  const notify = useToaster();
  const [
    {
      data: { meta = {} as IWebsiteConfigs },
    },
  ] = useSettings();
  const [searchParams] = useSearchParams();
  const [currentUser] = useCurrentUser();
  const { sendEvent } = useAnalytics();
  const [walletInfo, setWalletInfo] = useState('');
  const [info, setInfo] = useState({});
  const [infoName, setInfoName] = useState('');
  const [infoImage, setInfoImage] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const { translate } = useTranslates();

  const handler = useCallback(
    async (amount: number, sourceId: number) => {
      const result = await PaymentMethodsService.getPaymentMethodExtraInfo<
        [{ key: string; value: string; url?: string; error?: string }]
      >({
        amount,
        infoProperties: getInfoProperties(),
        sourceId,
        type: 'deposit',
      });
      const hasError = result.find(({ error }) => error);
      if (hasError) {
        notify({
          type: 'error',
          message: translate(hasError.error),
        });
        return;
      }
      setWalletInfo(result.find(({ key }) => key === 'account').value);
      setInfoImage(result.find(({ key }) => key === 'image').value);
      setInfoName(result.find(({ key }) => key === 'name').value);
      setInfo(result.find(({ key }) => key === 'info').value);
      const ticketId = result.find(({ key }) => key === 'ticket')?.value;
      setValue('ticketId', ticketId);
      const url = result.find(({ url }) => !!url)?.url;

      if (!url) {
        setRedirectUrl('');
        setShowConfirmation(true);
      }

      if (url) {
        setRedirectUrl(url);
        setShowConfirmation(false);
        const urlObj = new URL(url as string);
        const paymentId = urlObj.searchParams.get('paymentId');
        if (!paymentId) {
          return;
        }
        localStorage.setItem(`deposit_methodId_${paymentId}`, sourceId.toString());
        localStorage.setItem(`deposit_amount_${paymentId}`, amount.toString());
        localStorage.setItem(`deposit_ticketId_${paymentId}`, ticketId);
      }
    },
    [getInfoProperties]
  );

  const originalFieldNames = useMemo(() => Object.keys(getInfoProperties()), [getInfoProperties]);
  const areAllFieldsFilled = originalFieldNames.every((fieldName) => Boolean(watch(fieldName)));

  const debouncedPaymentMethodExtraInfo = useDebounce(handler, PAYMENT_INFO_DELAY);

  useMount(() => {
    changeActionButtonVisibility(false);
    changeAmountFieldVisibility(true);
  });

  useEffect(() => {
    if (amount && areAllFieldsFilled) {
      debouncedPaymentMethodExtraInfo(amount, paymentMethod.method.id);
    }
  }, [amount, areAllFieldsFilled, paymentMethod.method.id, debouncedPaymentMethodExtraInfo]);

  const handleChangeFile = (fileUrl) => {
    setValue('confirmationImage', fileUrl);
  };

  const amountRef = useRef(amount);
  useEffect(() => {
    if (amountRef.current !== amount) {
      setValue('confirmationCode', '');
      setValue('confirmationImage', '');
      setValue('ticketId', '');
      setWalletInfo('');
    }
    amountRef.current = amount;
  }, [amount]);

  const isDepositButtonEnabled =
    (!!confirmationCode || !!redirectUrl) && !!ticketId && !!walletInfo && !!amount && !isImageLoading;

  const deposit = useCallback(async () => {
    setLoading(true);
    const data = getValues();

    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }

    const successMessage = 'Deposit request has been sent successfully';
    const errorMessage = 'Deposit request has been failed';

    try {
      const response = await PaymentMethodsService.deposit({
        ...data,
        sourceId: paymentMethod.method.id,
        infoProperties: getInfoProperties(),
        query: searchParams.toString(),
      });
      sendEvent({
        [AnalyticsKey.pageView]: AnalyticsPageViewEvents.deposit,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        sales: {
          [AnalyticsKey.currency]: currentUser.data.currency.code,
          [AnalyticsKey.amount]: amount,
        },
      });
      notify({
        ...(!response && { type: 'error' }),
        message: response ? successMessage : errorMessage,
      });

      response && onClose();
    } catch {
      notify({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [redirectUrl]);

  const copyWalletInfo = () => {
    navigator.clipboard.writeText(walletInfo);
    notify({
      message: translate('copied_to_clipboard'),
    });
  };


  return (
    <>
      {showConfirmation && (
        <>
          {!!walletInfo && (
              <Grid item xs={12} display="flex" alignItems="center" gap={1}>
                <Typography>
                  {translate('wallet_info')}: {walletInfo}
                </Typography>
                <FileCopyIcon sx={{ cursor: 'pointer', fontSize: 18, color: 'primary.main' }} onClick={copyWalletInfo} />
              </Grid>

          )}
          {!!Object.keys(info).length &&
            Object.keys(info).map((key) => (
              <Grid item xs={12} display="flex" gap={2} key={key}>
                <Typography>{translate(key)}:</Typography>
                <Typography>{info[key]}</Typography>
              </Grid>
            ))}
          {!!infoName && (
            <Grid item xs={12} display="flex" gap={2}>
              <Typography>{translate('automatic_deposit_name')}</Typography>
              <Typography>{infoName}</Typography>
            </Grid>
          )}
          {!!infoImage && (
            <Grid item xs={12} display="flex" justifyContent="center">
              <img src={infoImage} width={250} />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormInput
              label={translate('confirmation_code')}
              disabled={!walletInfo}
              name="confirmationCode"
              required
              control={control}
            />
          </Grid>
          <Grid item xs={12} justifyContent="center" display="grid">
            <AttachFile onChange={handleChangeFile} setIsImageLoading={setIsImageLoading}>
              <Button disabled={!walletInfo} variant="contained">
                {translate('confirmation_image')}
              </Button>
            </AttachFile>
          </Grid>
        </>
      )}
      <Grid item xs={12} display="flex" justifyContent="center">
        <LoadingButton
          loading={loading}
          disabled={!isDepositButtonEnabled}
          variant="contained"
          style={{
            color: meta.customColors?.paymentModalAction || 'success',
            backgroundColor: meta.customColors?.paymentModalActionBg || 'primary.main',
          }}
          onClick={deposit}
          sx={{ mt: 3 }}
          type="submit"
        >
          {translate('deposit_now')}
        </LoadingButton>
      </Grid>
    </>
  );
};

export default AutomaticDeposit;
